/**
 * The marker a matter's reference must carry before it may be marked as a demo
 * matter.
 *
 * A demo matter is never billed, is never synced to the firm's practice
 * management system, has every outbound external email redirected back to the
 * user instead of its real recipient, and runs its title searches against the
 * Dye & Durham sandbox rather than the real registry. None of that is visible on
 * the matter itself, so the reference is where the decision is written down — and
 * requiring it is what stops a real matter being silenced by a stray click.
 *
 * **This mirrors the backend rule and is not the guard.** The server re-checks it
 * on every write and rejects a mismatch with a 400
 * (`backend/src/matters/constants/demo-matter.constants.ts` — keep the two in
 * step). What this rule decides here is only whether to *offer* the checkbox.
 */
export const DEMO_MATTER_REFERENCE_MARKER = 'DEMO';

/**
 * Whether a matter carrying `reference` may be marked as a demo matter.
 *
 * Case-insensitive, and a plain substring test rather than a prefix: firms number
 * their own matters, so we require only that the reference names the matter as a
 * demo somewhere, not that it starts that way.
 */
export function matterReferenceAllowsDemoFlag(
  reference: string | null | undefined
): boolean {
  if (!reference) return false;
  return reference.toUpperCase().includes(DEMO_MATTER_REFERENCE_MARKER);
}

/**
 * The value a form should actually submit for `isDemo`, given the reference it
 * holds and the state of its checkbox.
 *
 * Every call site goes through this rather than sending its raw checkbox state,
 * because the two can drift: `DemoMatterCheckbox` stops rendering the moment the
 * reference stops qualifying, and a box that was ticked before the user edited
 * the reference would otherwise keep submitting `true` from behind a control
 * nobody can see. The server would reject that with a 400 — correct, but a
 * confusing way to learn that renaming the matter turned the option off.
 */
export function resolveDemoMatterFlag(
  reference: string | null | undefined,
  checked: boolean | undefined
): boolean {
  return matterReferenceAllowsDemoFlag(reference) && checked === true;
}
